import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { tagAtPatchOnCreate } from '../utils/tagTimestamps';

// Each person's "in cinema" checkbox only means something once they're tagged, and defaults to
// whatever the general Cinema checkbox says at that moment (the common case: everyone tagged was
// at the same cinema viewing). It stays independently editable after that, so a later rewatch at
// home with someone else doesn't retroactively claim they were in the cinema too.
const PERSON_CINEMA_FIELD = {
  watchedWithJoy: 'watchedWithJoyCinema',
  watchedWithRick: 'watchedWithRickCinema',
  watchedWithLisa: 'watchedWithLisaCinema',
  watchedWithGionnan: 'watchedWithGionnanCinema',
  watchedWithYoussra: 'watchedWithYoussraCinema',
};

const WITH_PEOPLE = [
  { field: 'watchedWithJoy', label: 'Joy <3' },
  { field: 'watchedWithRick', label: 'Rick' },
  { field: 'watchedWithLisa', label: 'Lisa' },
  { field: 'watchedWithGionnan', label: 'Gionnan' },
  { field: 'watchedWithYoussra', label: 'Youssra' },
];

function MovieForm({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const [todoItemId] = useState(prefill.todoItemId || null);
  const [prefillPosterUrl] = useState(prefill.posterUrl || null);
  const [form, setForm] = useState({
    title: prefill.title || '',
    rating: '',
    thoughts: '',
    marvel: false,
    dc: false,
    cinema: false,
    watchedWithJoy: false,
    watchedWithRick: false,
    watchedWithLisa: false,
    watchedWithGionnan: false,
    watchedWithYoussra: false,
    watchedWithJoyCinema: false,
    watchedWithRickCinema: false,
    watchedWithLisaCinema: false,
    watchedWithGionnanCinema: false,
    watchedWithYoussraCinema: false,
  });
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (type === 'checkbox' && PERSON_CINEMA_FIELD[name]) {
        next[PERSON_CINEMA_FIELD[name]] = checked ? prev.cinema : false;
      }
      if (type === 'checkbox' && name === 'cinema') {
        WITH_PEOPLE.forEach(({ field }) => {
          if (prev[field]) next[PERSON_CINEMA_FIELD[field]] = checked;
        });
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.rating) {
      setStatus('Please fill in a title and rating.');
      return;
    }

    setLoading(true);
    setStatus('');

    try {
      let posterUrl = null;
      if (poster) posterUrl = await uploadPoster(poster);
      else if (prefillPosterUrl) posterUrl = prefillPosterUrl;

      const payload = {
        title: form.title,
        rating: parseFloat(form.rating),
        marvel: form.marvel,
        dc: form.dc,
        cinema: form.cinema,
        watched_with_joy: form.watchedWithJoy,
        watched_with_rick: form.watchedWithRick,
        watched_with_lisa: form.watchedWithLisa,
        watched_with_gionnan: form.watchedWithGionnan,
        watched_with_youssra: form.watchedWithYoussra,
        watched_with_joy_cinema: form.watchedWithJoyCinema,
        watched_with_rick_cinema: form.watchedWithRickCinema,
        watched_with_lisa_cinema: form.watchedWithLisaCinema,
        watched_with_gionnan_cinema: form.watchedWithGionnanCinema,
        watched_with_youssra_cinema: form.watchedWithYoussraCinema,
        thoughts: form.thoughts || '',
        ...tagAtPatchOnCreate([
          { isOn: form.watchedWithJoy, atField: 'watched_with_joy_at' },
          { isOn: form.watchedWithRick, atField: 'watched_with_rick_at' },
          { isOn: form.watchedWithLisa, atField: 'watched_with_lisa_at' },
          { isOn: form.watchedWithGionnan, atField: 'watched_with_gionnan_at' },
          { isOn: form.watchedWithYoussra, atField: 'watched_with_youssra_at' },
        ]),
      };

      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/movies`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create');

      if (todoItemId) {
        await fetch(`${SUPABASE_URL}/rest/v1/todo_movies?id=eq.${todoItemId}`, {
          method: 'DELETE',
          headers: supabaseHeaders(true),
        });
      }

      setStatus('Saved :D');
      if (onClose) {
        setTimeout(() => onClose(), 1500);
      } else {
        setTimeout(() => navigate('/Movies'), 1500);
      }
    } catch (err) {
      console.error(err);
      setStatus(`Something went wrong: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (onClose) {
    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ fontFamily: 'angelWish', fontSize: '2rem', letterSpacing: '0.06em', marginBottom: 0 }}>Add Movie</h2>
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Rating *</label>
          <input
            type="number"
            name="rating"
            value={form.rating}
            onChange={handleChange}
            min="1"
            max="10"
            step="0.1"
            required
          />
        </div>

        <div className="form-group">
          <label>Thoughts</label>
          <textarea name="thoughts" value={form.thoughts} onChange={handleChange} rows={4} />
        </div>

        <div className="form-group">
          <label>Poster</label>
          {(posterPreview || (prefillPosterUrl && !poster)) && (
            <img src={posterPreview || prefillPosterUrl} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
          )}
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setPoster(f); setPosterPreview(f ? URL.createObjectURL(f) : null); }} />
        </div>

        <div className="form-group">
          <label>Marvel/DC</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="marvel" checked={form.marvel} onChange={handleChange} /> Marvel</label>
            <label><input type="checkbox" name="dc" checked={form.dc} onChange={handleChange} /> DC</label>
          </div>
        </div>

        <div className="form-group">
          <label>Cinema</label>
          <div className="form-checkboxes">
            <input type="checkbox" name="cinema" checked={form.cinema} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label>With</label>
          <div className="form-checkboxes form-checkboxes--people">
            {WITH_PEOPLE.map(({ field, label }) => (
              <div className="with-person-row" key={field}>
                <label><input type="checkbox" name={field} checked={form[field]} onChange={handleChange} /> {label}</label>
                {form.cinema && (
                  <label className="cinema-subcheck">
                    <input
                      type="checkbox"
                      name={PERSON_CINEMA_FIELD[field]}
                      checked={form[PERSON_CINEMA_FIELD[field]]}
                      disabled={!form[field]}
                      onChange={handleChange}
                    /> C
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {status && <p className="form-status">{status}</p>}
        <div className="edit-modal-actions">
          <button type="button" className="form-submit" onClick={onClose} style={{ background: 'white', color: 'black', WebkitTextFillColor: 'black' }}>Cancel</button>
          <button type="submit" disabled={loading} className="form-submit">
            {loading ? 'Loading...' : 'Add Movie'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="app-container">
      <form onSubmit={handleSubmit} className="movie-form">
        <h1 style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', marginTop: 0, marginBottom: 0 }}>Add Movie</h1>
        <div className="form-group">
          <label>Title *</label>
          <input type="text" name="title" value={form.title} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Rating *</label>
          <input
            type="number"
            name="rating"
            value={form.rating}
            onChange={handleChange}
            min="1"
            max="10"
            step="0.1"
            required
          />
        </div>

        <div className="form-group">
          <label>Thoughts</label>
          <textarea name="thoughts" value={form.thoughts} onChange={handleChange} rows={4} />
        </div>

        <div className="form-group">
          <label>Poster</label>
          {(posterPreview || (prefillPosterUrl && !poster)) && (
            <img src={posterPreview || prefillPosterUrl} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
          )}
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setPoster(f); setPosterPreview(f ? URL.createObjectURL(f) : null); }} />
        </div>

        <div className="form-group">
          <label>Marvel/DC</label>
          <div className="form-checkboxes">
            <label><input type="checkbox" name="marvel" checked={form.marvel} onChange={handleChange} /> Marvel</label>
            <label><input type="checkbox" name="dc" checked={form.dc} onChange={handleChange} /> DC</label>
          </div>
        </div>

        <div className="form-group">
          <label>Cinema</label>
          <div className="form-checkboxes">
            <input type="checkbox" name="cinema" checked={form.cinema} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label>With</label>
          <div className="form-checkboxes form-checkboxes--people">
            {WITH_PEOPLE.map(({ field, label }) => (
              <div className="with-person-row" key={field}>
                <label><input type="checkbox" name={field} checked={form[field]} onChange={handleChange} /> {label}</label>
                {form.cinema && (
                  <label className="cinema-subcheck">
                    <input
                      type="checkbox"
                      name={PERSON_CINEMA_FIELD[field]}
                      checked={form[PERSON_CINEMA_FIELD[field]]}
                      disabled={!form[field]}
                      onChange={handleChange}
                    /> C
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading} className="form-submit">
          {loading ? 'Loading...' : 'Add Movie'}
        </button>

        {status && <p className="form-status">{status}</p>}
      </form>
    </div>
  );
}

export default MovieForm;
